import { Plus } from "lucide-react";

/**
 * HeaderPill — pilule utilisée dans les en-têtes de page et boutons
 * d'action rapide. Extraite pour dédupliquer le CSS entre
 * Documents / Tâches / AVI et accélérer le dev des futures pages.
 *
 * Spec rev177 (2026-02) — variant "pill" (par défaut) :
 *   h 36 px, padding 4/12/4/4, radius 18, font 11.5, pastille 26×26
 *   avec icône couleur module. Boutons "Vue globale", "Corbeille",
 *   "Exporter", "Activité récente", etc.
 *
 * Spec rev153 (2026-02-07) — variant "kpi" :
 *   Tuile pleine module color, h 60 px (aligne les tuiles KPI de la
 *   barre d'entête). Grand "+" 26 px + label uppercase 10 px en
 *   dessous. Remplace les buttons ad-hoc de Tasks.jsx / Documents.jsx
 *   / Avi.jsx pour uniformiser le style et faciliter futurs modules.
 *
 * @param {object}  props
 * @param {"pill"|"kpi"} [props.variant="pill"]  Style visuel
 * @param {React.ComponentType} [props.icon]      Icône lucide (pill) — ignorée en kpi
 * @param {string}  [props.iconBg]                Couleur pastille (pill) OU fond tuile (kpi)
 * @param {string}  props.label                   Libellé (pill : texte à droite · kpi : texte SOUS le "+")
 * @param {string}  [props.symbol="+"]            Symbole affiché en gros pour variant kpi
 * @param {string}  [props.textColor]             Couleur du texte + symbole en kpi (défaut auto)
 * @param {boolean} [props.disabled=false]        Grise + désactive
 * @param {Function} props.onClick                Handler clic
 * @param {string}  [props.title]                 Tooltip
 * @param {string}  [props.testid]                data-testid
 * @param {boolean} [props.iconMuted=false]       Icône désaturée (pill uniquement)
 * @param {boolean} [props.active=false]          État actif (pill uniquement)
 * @param {string}  [props.activeGlow]            Glow actif (pill uniquement)
 */
export default function HeaderPill({
  variant = "pill",
  icon: Icon, iconBg, label, onClick, title, testid,
  iconMuted = false, active = false, activeGlow,
  disabled = false, symbol = "+", textColor, flat = false, width,
}) {
  if (variant === "kpi" && flat) {
    // iter459 pt 13.2 — tuile plate : fond plein, radius 9, sans ombre, icône Plus 22 px (trait 2.6), survol brightness 1.08.
    return (
      <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} title={title} data-testid={testid}
        className="flex flex-col items-center justify-center shrink-0 transition-[filter] hover:brightness-[1.08] disabled:hover:brightness-100"
        style={{ width, alignSelf: "stretch", background: iconBg, color: "#fff", borderRadius: 9, padding: "6px 4px", gap: 4, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1 }}>
        <Plus size={22} strokeWidth={2.6} color="#fff" aria-hidden="true" />
        <span className="uppercase whitespace-nowrap" style={{ fontSize: 10, letterSpacing: ".06em", fontWeight: 700, color: "#fff" }}>{label}</span>
      </button>
    );
  }
  if (variant === "kpi") {
    // Spec iter87 (Feb 2026) — texte TOUJOURS blanc sur fond coloré plein,
    // en cohérence avec les boutons "+ Document" / "+ Validation" du reste
    // de l'app. La règle spéciale jaune → texte foncé a été retirée
    // (contraste rétabli via ``iconBg`` sombre en général, mais un léger
    // ``text-shadow`` restitue la lisibilité sur les couleurs très claires).
    const fg = textColor || "#FFFFFF";
    // Ombre projetée = iconBg à 28% pour attirer l'œil.
    const glow = iconBg ? `${iconBg}47` : "rgba(0,0,0,0.28)";
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className="flex flex-col items-center justify-center rounded-md transition-all hover:brightness-110 disabled:hover:brightness-100"
        style={{
          background: iconBg,
          color: fg,
          padding: "6px 4px",
          minHeight: 60,
          cursor: disabled ? "not-allowed" : "pointer",
          border: "1px solid transparent",
          fontWeight: 700,
          boxShadow: disabled ? "none" : `0 2px 10px ${glow}`,
          opacity: disabled ? 0.55 : 1,
        }}
        data-testid={testid}
        title={title}
      >
        <span aria-hidden="true" className="tabular-nums"
          style={{ fontSize: 26, lineHeight: 1, color: fg, fontWeight: 700 }}>
          {symbol}
        </span>
        <span className="uppercase mt-1 whitespace-nowrap"
          style={{ fontSize: 10, letterSpacing: "0.03em", fontWeight: 700, color: fg }}>
          {label}
        </span>
      </button>
    );
  }

  // ── variant "pill" (défaut, rev177 inchangé) ─────────────────────
  const activeColor = activeGlow || iconBg;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 font-semibold transition-all hover:brightness-110 disabled:opacity-40"
      style={{
        height: 36, padding: "4px 12px 4px 4px",
        borderRadius: 18, fontSize: 11.5,
        background: "#232323",
        border: `1px solid ${active ? activeColor : "#333"}`,
        color: "#E5E5E5",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      data-testid={testid}
      title={title}
    >
      <span
        className="rounded-full flex items-center justify-center shrink-0"
        style={{
          width: 26, height: 26,
          background: iconBg, color: "#FFFFFF",
          opacity: iconMuted ? 0.55 : 1,
          boxShadow: active ? `0 0 0 2px ${activeColor}55` : "none",
        }}
      >
        {Icon && <Icon size={13} strokeWidth={2.4} />}
      </span>
      {label}
    </button>
  );
}
